import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, User, Mail, Phone, Building2, Briefcase, MapPin, Tag, Calendar as CalendarIcon, Wallet, ChevronDown, ChevronUp, FileText } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Textarea } from '@/components/ui/textarea';
import { format } from 'date-fns';
import axios from 'axios';
import { WalletAddressManager, CryptoHoldingsManager, InvestmentFocusManager } from './CryptoProfileFields';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CreateContactModal = ({ isOpen, onClose, onSubmit, editContact = null }) => {
  // Determine edit context: 'contact' for Personal Info, 'crypto' for Crypto & Investment Profile, 'preferences' for Preferences, null for create new
  const editContext = editContact?.editContext || null;
  const isEditingPersonalInfo = editContext === 'contact';
  const isEditingCryptoInfo = editContext === 'crypto';
  const isEditingPreferences = editContext === 'preferences';
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    role: '',
    status: 'active',
    type: 'investor',
    dealSize: '',
    location: '',
    tags: '',
    lastContact: null,
    // New crypto and profile fields
    walletAddresses: [],
    cryptoHoldings: {},
    connectionType: 'external',
    notes: '',
    preferencesNotes: '',
    socialLinkedin: '',
    socialTwitter: '',
    investmentFocus: []
  });

  const [showCryptoSection, setShowCryptoSection] = useState(false);

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availableTags, setAvailableTags] = useState([]);
  const [isLoadingTags, setIsLoadingTags] = useState(false);

  // Fetch available contact tags
  useEffect(() => {
    if (isOpen) {
      fetchContactTags();
    }
  }, [isOpen]);

  const fetchContactTags = async () => {
    setIsLoadingTags(true);
    try {
      const response = await axios.get(`${API_URL}/api/tags?tag_type=contacts`);
      setAvailableTags(response.data);
    } catch (error) {
      console.error('Error fetching tags:', error);
    } finally {
      setIsLoadingTags(false);
    }
  };

  useEffect(() => {
    if (editContact) {
      setFormData({
        firstName: editContact.firstName || '',
        lastName: editContact.lastName || '',
        email: editContact.email || '',
        phone: editContact.phone || '',
        company: editContact.company || '',
        role: editContact.role || '',
        status: editContact.status || 'active',
        type: editContact.type || 'investor',
        dealSize: editContact.dealSize || '',
        location: editContact.location || '',
        tags: editContact.tags ? editContact.tags.map(t => t.name).join(', ') : '',
        lastContact: editContact.lastContact ? new Date(editContact.lastContact) : null,
        // New crypto and profile fields
        walletAddresses: editContact.walletAddresses || [],
        cryptoHoldings: editContact.cryptoHoldings || {},
        connectionType: editContact.connectionType || 'external',
        notes: editContact.notes || '',
        preferencesNotes: editContact.preferencesNotes || '',
        socialLinkedin: editContact.socialLinkedin || '',
        socialTwitter: editContact.socialTwitter || '',
        investmentFocus: editContact.investmentFocus || []
      });
      // Show crypto section if there's crypto data or if editing crypto info
      if (editContext === 'crypto' || editContact.walletAddresses?.length > 0 || Object.keys(editContact.cryptoHoldings || {}).length > 0) {
        setShowCryptoSection(true);
      }
    } else {
      resetForm();
    }
  }, [editContact, isOpen, editContext]);

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      company: '',
      role: '',
      status: 'active',
      type: 'investor',
      dealSize: '',
      location: '',
      tags: '',
      lastContact: null,
      // New crypto and profile fields
      walletAddresses: [],
      cryptoHoldings: {},
      connectionType: 'external',
      notes: '',
      preferencesNotes: '',
      socialLinkedin: '',
      socialTwitter: '',
      investmentFocus: []
    });
    setErrors({});
    setShowCryptoSection(false);
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    // Validate tags
    if (formData.tags.trim()) {
      const tagNames = formData.tags.split(',').map(t => t.trim()).filter(t => t);
      const invalidTags = tagNames.filter(tagName => 
        !availableTags.some(tag => tag.name.toLowerCase() === tagName.toLowerCase())
      );
      
      if (invalidTags.length > 0) {
        newErrors.tags = `Invalid tag(s): ${invalidTags.join(', ')}. Please use existing Contact Tags.`;
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;
    
    setIsSubmitting(true);
    
    try {
      // Convert tag names to tag IDs
      const tagNames = formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(t => t) : [];
      const tagIds = tagNames.map(tagName => {
        const tag = availableTags.find(t => t.name.toLowerCase() === tagName.toLowerCase());
        return tag ? tag.id : null;
      }).filter(id => id !== null);

      const submitData = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim() || null,
        company: formData.company.trim() || null,
        role: formData.role.trim() || null,
        status: formData.status,
        type: formData.type,
        dealSize: formData.dealSize || null,
        location: formData.location.trim() || null,
        tags: tagIds,
        lastContact: formData.lastContact ? format(formData.lastContact, 'yyyy-MM-dd') : null,
        // New crypto and profile fields
        walletAddresses: formData.walletAddresses,
        cryptoHoldings: formData.cryptoHoldings,
        connectionType: formData.connectionType,
        notes: formData.notes.trim() || null,
        preferencesNotes: formData.preferencesNotes.trim() || null,
        socialLinkedin: formData.socialLinkedin.trim() || null,
        socialTwitter: formData.socialTwitter.trim() || null,
        investmentFocus: formData.investmentFocus
      };

      await onSubmit(submitData, editContact?.id);
      resetForm();
      onClose();
    } catch (error) {
      console.error('Error submitting contact:', error);
      // Show error from backend if it's a tag validation error
      if (error.response?.data?.detail) {
        setErrors(prev => ({ ...prev, tags: error.response.data.detail }));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.2 }}
          className="relative w-full max-w-4xl max-h-[95vh] bg-[#0f1115] border border-white/10 rounded-lg shadow-2xl flex flex-col"
        >
          {/* Header - Fixed */}
          <div className="p-4 border-b border-white/10 shrink-0">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">
                {editContact 
                  ? isEditingPersonalInfo 
                    ? 'Edit Personal Info' 
                    : isEditingCryptoInfo 
                      ? 'Edit Crypto & Investment Profile'
                      : isEditingPreferences
                        ? 'Edit Preferences'
                        : 'Edit Contact'
                  : 'Add New Contact'}
              </h2>
              <button
                onClick={onClose}
                className="text-zinc-400 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Form - Scrollable */}
          <div className="overflow-y-auto flex-1">
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
            {/* Personal Information - Show when creating new or editing personal info */}
            {(!editContact || isEditingPersonalInfo) && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-zinc-300 flex items-center gap-2">
                <User size={16} />
                Personal Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="firstName" className="text-zinc-300 mb-1.5">
                    First Name <span className="text-red-400">*</span>
                  </Label>
                  <Input
                    id="firstName"
                    value={formData.firstName}
                    onChange={(e) => handleChange('firstName', e.target.value)}
                    className={`bg-[#0a0a0a] border-white/10 text-zinc-200 ${errors.firstName ? 'border-red-500' : ''}`}
                    placeholder="John"
                  />
                  {errors.firstName && <p className="text-xs text-red-400 mt-1">{errors.firstName}</p>}
                </div>
                <div>
                  <Label htmlFor="lastName" className="text-zinc-300 mb-1.5">
                    Last Name <span className="text-red-400">*</span>
                  </Label>
                  <Input
                    id="lastName"
                    value={formData.lastName}
                    onChange={(e) => handleChange('lastName', e.target.value)}
                    className={`bg-[#0a0a0a] border-white/10 text-zinc-200 ${errors.lastName ? 'border-red-500' : ''}`}
                    placeholder="Doe"
                  />
                  {errors.lastName && <p className="text-xs text-red-400 mt-1">{errors.lastName}</p>}
                </div>
              </div>
            </div>
            )}

            {/* Contact Information - Show when creating new or editing personal info */}
            {(!editContact || isEditingPersonalInfo) && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-zinc-300 flex items-center gap-2">
                <Mail size={16} />
                Contact Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="email" className="text-zinc-300 mb-1.5">
                    Email <span className="text-red-400">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className={`bg-[#0a0a0a] border-white/10 text-zinc-200 ${errors.email ? 'border-red-500' : ''}`}
                    placeholder="john.doe@example.com"
                  />
                  {errors.email && <p className="text-xs text-red-400 mt-1">{errors.email}</p>}
                </div>
                <div>
                  <Label htmlFor="phone" className="text-zinc-300 mb-1.5">
                    Phone
                  </Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    className="bg-[#0a0a0a] border-white/10 text-zinc-200"
                    placeholder="+1 (555) 000-0000"
                  />
                </div>
              </div>
            </div>
            )}

            {/* Company Information - Show when creating new or editing personal info */}
            {(!editContact || isEditingPersonalInfo) && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-zinc-300 flex items-center gap-2">
                <Building2 size={16} />
                Company Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="company" className="text-zinc-300 mb-1.5">
                    Company
                  </Label>
                  <Input
                    id="company"
                    value={formData.company}
                    onChange={(e) => handleChange('company', e.target.value)}
                    className="bg-[#0a0a0a] border-white/10 text-zinc-200"
                    placeholder="Acme Corp"
                  />
                </div>
                <div>
                  <Label htmlFor="role" className="text-zinc-300 mb-1.5">
                    Role
                  </Label>
                  <Input
                    id="role"
                    value={formData.role}
                    onChange={(e) => handleChange('role', e.target.value)}
                    className="bg-[#0a0a0a] border-white/10 text-zinc-200"
                    placeholder="CEO"
                  />
                </div>
              </div>
            </div>
            )}

            {/* Classification - Show when creating new or editing personal info */}
            {(!editContact || isEditingPersonalInfo) && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-zinc-300 flex items-center gap-2">
                <Briefcase size={16} />
                Classification
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="status" className="text-zinc-300 mb-1.5">
                    Status
                  </Label>
                  <Select value={formData.status} onValueChange={(value) => handleChange('status', value)}>
                    <SelectTrigger className="bg-[#0a0a0a] border-white/10 text-zinc-300">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0f1115] border-white/10">
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="type" className="text-zinc-300 mb-1.5">
                    Type
                  </Label>
                  <Select value={formData.type} onValueChange={(value) => handleChange('type', value)}>
                    <SelectTrigger className="bg-[#0a0a0a] border-white/10 text-zinc-300">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0f1115] border-white/10">
                      <SelectItem value="investor">Investor</SelectItem>
                      <SelectItem value="portfolio">Portfolio</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            )}

            {/* Additional Details - Show when creating new or editing personal info */}
            {(!editContact || isEditingPersonalInfo) && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-zinc-300 flex items-center gap-2">
                <MapPin size={16} />
                Additional Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="dealSize" className="text-zinc-300 mb-1.5">
                    Deal Size (USD)
                  </Label>
                  <Input
                    id="dealSize"
                    type="number"
                    value={formData.dealSize}
                    onChange={(e) => handleChange('dealSize', e.target.value)}
                    className="bg-[#0a0a0a] border-white/10 text-zinc-200"
                    placeholder="2530000"
                    min="0"
                    step="1"
                  />
                  <p className="text-xs text-zinc-500 mt-1">Enter amount in USD (e.g., 2530000)</p>
                </div>
                <div>
                  <Label htmlFor="location" className="text-zinc-300 mb-1.5">
                    Location
                  </Label>
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => handleChange('location', e.target.value)}
                    className="bg-[#0a0a0a] border-white/10 text-zinc-200"
                    placeholder="San Francisco, CA"
                  />
                </div>
                <div>
                  <Label htmlFor="lastContact" className="text-zinc-300 mb-1.5">
                    Last Contact
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal bg-[#0a0a0a] border-white/10 text-zinc-200 hover:bg-[#0a0a0a] hover:text-zinc-200"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.lastContact ? format(formData.lastContact, 'PPP') : <span className="text-zinc-500">Pick a date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-[#0f1115] border-white/10" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.lastContact}
                        onSelect={(date) => handleChange('lastContact', date)}
                        initialFocus
                        className="bg-[#0f1115] text-zinc-200"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label htmlFor="tags" className="text-zinc-300 mb-1.5">
                    Tags (comma-separated)
                  </Label>
                  <Input
                    id="tags"
                    value={formData.tags}
                    onChange={(e) => handleChange('tags', e.target.value)}
                    className={`bg-[#0a0a0a] border-white/10 text-zinc-200 ${errors.tags ? 'border-red-500' : ''}`}
                    placeholder="Series A, Tech, Healthcare"
                    disabled={isLoadingTags}
                  />
                  {errors.tags && <p className="text-xs text-red-400 mt-1">{errors.tags}</p>}
                  {!isLoadingTags && availableTags.length > 0 && (
                    <div className="mt-2 text-xs text-zinc-500">
                      <span className="font-medium">Available:</span>{' '}
                      {availableTags.map((tag, idx) => (
                        <span key={tag.id}>
                          {tag.name}
                          {idx < availableTags.length - 1 ? ', ' : ''}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            )}

            {/* Crypto & Profile Section - Show when creating new or editing crypto info */}
            {(!editContact || isEditingCryptoInfo) && (
            <div className="space-y-3 border-t border-white/10 pt-4">
              <button
                type="button"
                onClick={() => setShowCryptoSection(!showCryptoSection)}
                className="w-full flex items-center justify-between text-sm font-medium text-zinc-300 hover:text-white transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Wallet size={16} />
                  Crypto & Investment Profile
                </div>
                {showCryptoSection ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>

              {showCryptoSection && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-4 pt-3"
                >
                  {/* Connection Type & Social Links */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <Label htmlFor="connectionType" className="text-zinc-300 mb-1.5">
                        Connection Type
                      </Label>
                      <Select
                        value={formData.connectionType}
                        onValueChange={(value) => handleChange('connectionType', value)}
                      >
                        <SelectTrigger className="bg-[#0a0a0a] border-white/10 text-zinc-200">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="external">External</SelectItem>
                          <SelectItem value="internal">Internal</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="socialLinkedin" className="text-zinc-300 mb-1.5">
                        LinkedIn
                      </Label>
                      <Input
                        id="socialLinkedin"
                        value={formData.socialLinkedin}
                        onChange={(e) => handleChange('socialLinkedin', e.target.value)}
                        className="bg-[#0a0a0a] border-white/10 text-zinc-200"
                        placeholder="linkedin.com/in/username"
                      />
                    </div>
                    <div>
                      <Label htmlFor="socialTwitter" className="text-zinc-300 mb-1.5">
                        Twitter/X
                      </Label>
                      <Input
                        id="socialTwitter"
                        value={formData.socialTwitter}
                        onChange={(e) => handleChange('socialTwitter', e.target.value)}
                        className="bg-[#0a0a0a] border-white/10 text-zinc-200"
                        placeholder="@username"
                      />
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <Label htmlFor="notes" className="text-zinc-300 mb-1.5">
                      Notes
                    </Label>
                    <Textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => handleChange('notes', e.target.value)}
                      className="bg-[#0a0a0a] border-white/10 text-zinc-200 min-h-[80px]"
                      placeholder="General notes about this contact..."
                    />
                  </div>

                  {/* Investment Focus */}
                  <div className="text-zinc-200">
                    <InvestmentFocusManager
                      investmentFocus={formData.investmentFocus}
                      onChange={(value) => handleChange('investmentFocus', value)}
                    />
                  </div>

                  {/* Wallet Addresses */}
                  <div className="text-zinc-200">
                    <WalletAddressManager
                      walletAddresses={formData.walletAddresses}
                      onChange={(value) => handleChange('walletAddresses', value)}
                    />
                  </div>

                  {/* Crypto Holdings */}
                  <div className="text-zinc-200">
                    <CryptoHoldingsManager
                      cryptoHoldings={formData.cryptoHoldings}
                      onChange={(value) => handleChange('cryptoHoldings', value)}
                    />
                  </div>
                </motion.div>
              )}
            </div>
            )}

            {/* Preferences Section - Show when creating new or editing preferences */}
            {(!editContact || isEditingPreferences) && (
            <div className="space-y-3 border-t border-white/10 pt-4">
              <h3 className="text-sm font-medium text-zinc-300 flex items-center gap-2">
                <FileText size={16} />
                Preferences & Notes
              </h3>
              <div>
                <Label htmlFor="preferencesNotes" className="text-zinc-300 mb-1.5">
                  Notes
                </Label>
                <Textarea
                  id="preferencesNotes"
                  value={formData.preferencesNotes}
                  onChange={(e) => handleChange('preferencesNotes', e.target.value)}
                  className="bg-[#0a0a0a] border-white/10 text-zinc-200 min-h-[200px] resize-y"
                  placeholder="Add any notes, preferences, or important information about this contact..."
                />
                <p className="text-xs text-zinc-500 mt-1">
                  Use this space to track communication preferences, important dates, or any other relevant information.
                </p>
              </div>
            </div>
            )}

            {/* Footer Actions */}
            <div className="flex items-center justify-end gap-3 pt-3 border-t border-white/10">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="bg-transparent border-white/10 text-zinc-300 hover:bg-white/5"
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Saving...' : editContact ? 'Update Contact' : 'Create Contact'}
              </Button>
            </div>
          </form>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default CreateContactModal;
